import { useSyncExternalStore } from 'react';

type FontSize = 'small' | 'medium' | 'large';
const storageKey = 'longjie.font-size';
const listeners = new Set<() => void>();
let listeningForStorage = false;

function parseFontSize(value: string | null): FontSize {
  return value === 'medium' || value === 'large' ? value : 'small';
}

function readSavedFontSize(): FontSize {
  try {
    return parseFontSize(window.localStorage.getItem(storageKey));
  } catch {
    return 'small';
  }
}

let fontSize = readSavedFontSize();

function applyFontSize(value: FontSize) {
  fontSize = value;
  document.documentElement.dataset.fontSize = value;
  for (const listener of listeners) listener();
}

// Apply before React's first paint, including login and body-level modal portals.
export function initializeFontSizePreference() {
  applyFontSize(readSavedFontSize());
  if (listeningForStorage) return;
  listeningForStorage = true;
  window.addEventListener('storage', (event) => {
    if (event.key === storageKey || event.key === null) {
      applyFontSize(readSavedFontSize());
    }
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

function setFontSize(value: FontSize) {
  try {
    window.localStorage.setItem(storageKey, value);
  } catch {
    // Still allow changing the current page when browser storage is unavailable.
  }
  applyFontSize(value);
}

export function useFontSizePreference() {
  const value = useSyncExternalStore(subscribe, () => fontSize, () => 'small' as const);
  return [value, setFontSize] as const;
}
